import React from "react";
import { localizeTitle } from "../utils/textDirection";

// The homepage's poster-style card (kicker rule, stacked title, optional
// byline rule, optional subtitle, optional image), extracted from
// HomePage.js so the same template/format/font can be reused anywhere a
// card should look like a hero-poster spread (e.g. issue topic cards on
// IssueDetailPage.js).
function toLines(value) {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
}

// The poster-scale title (clamp up to 4.6rem, line-height 0.9) reads
// great for the homepage's short 1-2 word placeholders ("دریچه" / "۱۲")
// but a real card title can overflow the card at that size in two
// different ways total character count alone doesn't catch: more words
// means more line-wraps even when each word is short (e.g. a four-word
// title), and — since English titles only break at word boundaries, not
// mid-word (see isLatinTitle) — a single long English word needs room to
// fit on its own line even when the title overall is short ("Manifesto"
// was getting the full poster size before this). The score below weighs
// letter count, word count, and a flat penalty for Latin titles (whose
// glyphs run wider than Persian's at the same size) together, then steps
// the title down through a few smaller tiers as that score grows.
function titleSizeTier(lines, isLatin) {
  const words = lines.join(" ").trim().split(/\s+/).filter(Boolean);
  const letters = words.join("").length;
  const score = letters + words.length * 3 + (isLatin ? 8 : 0);

  if (score > 30) return "hero-poster-title--sm";
  if (score > 22) return "hero-poster-title--md";
  if (score > 14) return "hero-poster-title--lg";
  return "";
}

function HeroPosterCard({
  kicker,
  title,
  byline,
  subtitle,
  imageSrc,
  imageAlt = "",
  titleTag: TitleTag = "h2",
  showRule = Boolean(byline),
  className = "",
}) {
  const { dir: titleDir, text: titleLines } = localizeTitle(toLines(title));
  const { dir: subtitleDir, text: subtitleLines } = localizeTitle(toLines(subtitle));
  const { dir: bylineDir, text: localizedByline } = localizeTitle(byline);
  const titleIsLatin = titleDir === "ltr";
  const titleClasses = ["hero-poster-title", titleSizeTier(titleLines, titleIsLatin), titleIsLatin ? "hero-poster-title--latin" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={className ? `hero-poster ${className}` : "hero-poster"}>
      <div className="hero-poster-topline">
        <span className="hero-poster-topline-seg" />
        <span className="hero-poster-kicker">{kicker}</span>
        <span className="hero-poster-topline-seg" />
      </div>

      <div className="hero-poster-body">
        <TitleTag className={titleClasses} dir={titleDir}>
          {titleLines.map((line, index) => (
            <span key={index}>{line}</span>
          ))}
        </TitleTag>

        {showRule && (
          <div className="hero-poster-rule">
            <span className="hero-poster-rule-dots" aria-hidden="true">
              ❖❖
            </span>
            <span className="hero-poster-rule-line" />
            {byline && (
              <span className="hero-poster-byline" dir={bylineDir}>
                {localizedByline}
              </span>
            )}
          </div>
        )}

        {subtitleLines.length > 0 && (
          <p className="hero-poster-subtitle" dir={subtitleDir}>
            {subtitleLines.map((line, index) => (
              <span key={index}>{line}</span>
            ))}
          </p>
        )}

        {imageSrc && <img className="hero-poster-image" src={imageSrc} alt={imageAlt} />}
      </div>
    </article>
  );
}

export default HeroPosterCard;
